import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import styled, { keyframes } from "styled-components";

import Modal from "src/components/Modal";
import { colors } from "src/styles";
import { ExportDateRangeOption, ExportWorkOrderField } from "src/pages/WorkRequests/types";
import { ExportWorkOrdersFormValues, ExportWorkOrdersModalProps } from "./types";

type FieldOption = {
  value: ExportWorkOrderField;
  label: string;
};

const DATE_RANGE_OPTIONS: { value: ExportDateRangeOption; label: string }[] = [
  { value: "custom", label: "Custom Entry" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "thisYear", label: "This Year" },
  { value: "lastYear", label: "Last Year" },
];

const FIELD_OPTIONS: FieldOption[] = [
  { value: "wonum", label: "Work Order Number" },
  { value: "client", label: "Client" },
  { value: "location", label: "Location" },
  { value: "asset", label: "Asset" },
  { value: "description", label: "Work Order Description" },
  { value: "workType", label: "Work Type" },
  { value: "serviceType", label: "Service Type" },
  { value: "priority", label: "Priority" },
  { value: "vendor", label: "Vendor" },
  { value: "teamMember", label: "Team Member" },
  { value: "clientPoNumber", label: "Client PO Number" },
  { value: "highRiskClassification", label: "High Risk Classification" },
  { value: "requestorFirstName", label: "Requestor First Name" },
  { value: "requestorEmail", label: "Requestor Email" },
];

const ALL_FIELDS = FIELD_OPTIONS.map((field) => field.value);

const DATE_RANGE_LABELS: Record<ExportDateRangeOption, string> = {
  custom: "Custom Entry",
  thisMonth: "This Month",
  lastMonth: "Last Month",
  thisYear: "This Year",
  lastYear: "Last Year",
};

const ExportWorkOrdersModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  title = "Export Work Orders",
  confirmText = "Export",
  cancelText = "Cancel",
}: ExportWorkOrdersModalProps) => {
  const [dateRangeOption, setDateRangeOption] = useState<ExportDateRangeOption>("thisMonth");
  const [fields, setFields] = useState<ExportWorkOrderField[]>(ALL_FIELDS);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setDateRangeOption("thisMonth");
    setFields(ALL_FIELDS);
    setFromDate(moment().startOf("month").toDate());
    setToDate(new Date());
    setValidationError("");
  }, [isOpen]);

  const onToggleField = (field: ExportWorkOrderField) => {
    setFields((prev) => (prev.includes(field) ? prev.filter((item) => item !== field) : [...prev, field]));
  };

  const onApproveClick: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    event.preventDefault();

    if (!fields.length) {
      setValidationError("Please select at least one field.");
      return;
    }

    if (dateRangeOption === "custom") {
      if (!fromDate || !toDate) {
        setValidationError("Please select both From and To dates for a custom range.");
        return;
      }

      if (moment(fromDate).isAfter(toDate, "day")) {
        setValidationError("From date cannot be after To date.");
        return;
      }
    }

    setValidationError("");

    const payload: ExportWorkOrdersFormValues = {
      fields,
      dateRangeOption,
      fromDate: dateRangeOption === "custom" && fromDate ? moment(fromDate).format("YYYY-MM-DD") : undefined,
      toDate: dateRangeOption === "custom" && toDate ? moment(toDate).format("YYYY-MM-DD") : undefined,
    };

    const isSuccess = await onSubmit(payload);
    if (isSuccess) {
      onClose();
    }
  };

  const isAllSelected = fields.length === ALL_FIELDS.length;
  const selectedDateRangeLabel = DATE_RANGE_LABELS[dateRangeOption];

  return (
    <Modal
      isShowing={isOpen}
      hide={onClose}
      title={title}
      onCancelClick={onClose}
      onApproveClick={onApproveClick}
      approveButtonText={confirmText}
      cancelButtonText={cancelText}
      approveButtonStyle="primary"
      cancelButtonStyle="secondary"
      disableApproveButton={isSubmitting}
      isLoadingApproveButton={isSubmitting}
      maxWidth="760px"
    >
      <ModalCanvas>
        <SummaryStrip>
          <SummaryMeta>
            <SummaryLabel>Configured Range</SummaryLabel>
            <SummaryValue>{selectedDateRangeLabel}</SummaryValue>
          </SummaryMeta>
          <SummaryDivider />
          <SummaryMeta>
            <SummaryLabel>Selected Fields</SummaryLabel>
            <SummaryValue>
              {fields.length} of {ALL_FIELDS.length}
            </SummaryValue>
          </SummaryMeta>
        </SummaryStrip>

        <SectionCard>
          <SectionHeader>
            <SectionTitle>Date Range</SectionTitle>
            <SectionSubTitle>Choose a window for the report data.</SectionSubTitle>
          </SectionHeader>

          <DateRangeSelect
            value={dateRangeOption}
            onChange={(event) => setDateRangeOption(event.target.value as ExportDateRangeOption)}
          >
            {DATE_RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </DateRangeSelect>

          {dateRangeOption === "custom" ? (
            <DateRangeRow>
              <DateField>
                <FieldLabel>From Date</FieldLabel>
                <StyledDatePicker
                  selected={fromDate}
                  onChange={(date: Date | null) => setFromDate(date)}
                  dateFormat="MM/dd/yyyy"
                  maxDate={toDate || new Date()}
                  placeholderText="Select from date"
                />
              </DateField>

              <DateField>
                <FieldLabel>To Date</FieldLabel>
                <StyledDatePicker
                  selected={toDate}
                  onChange={(date: Date | null) => setToDate(date)}
                  dateFormat="MM/dd/yyyy"
                  minDate={fromDate || undefined}
                  maxDate={new Date()}
                  placeholderText="Select to date"
                />
              </DateField>
            </DateRangeRow>
          ) : null}
        </SectionCard>

        <SectionCard>
          <FieldsHeader>
            <div>
              <SectionTitle>Fields</SectionTitle>
              <SectionSubTitle>Select exactly what should be included in Excel.</SectionSubTitle>
            </div>
            <InlineButton type="button" onClick={() => setFields(isAllSelected ? [] : ALL_FIELDS)}>
              {isAllSelected ? "Clear all" : "Select all"}
            </InlineButton>
          </FieldsHeader>

          <FieldsGrid>
            {FIELD_OPTIONS.map((field) => {
              const isChecked = fields.includes(field.value);
              return (
                <FieldChip key={field.value} $isChecked={isChecked}>
                  <HiddenCheckbox type="checkbox" checked={isChecked} onChange={() => onToggleField(field.value)} />
                  <FieldCheckMark $isChecked={isChecked}>{isChecked ? "\u2713" : ""}</FieldCheckMark>
                  <FieldText>{field.label}</FieldText>
                </FieldChip>
              );
            })}
          </FieldsGrid>
        </SectionCard>
      </ModalCanvas>
      {validationError ? <ValidationError>{validationError}</ValidationError> : null}
    </Modal>
  );
};

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ModalCanvas = styled.div`
  display: grid;
  gap: 10px;
`;

const SummaryStrip = styled.div`
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  align-items: center;
  border: 1px solid ${colors.grey.light12};
  padding: 10px 12px;
  background: linear-gradient(
    110deg,
    ${colors.grey.light16} 0%,
    ${colors.white.default} 55%,
    ${colors.grey.light14} 100%
  );
  animation: ${slideIn} 180ms ease;
`;

const SummaryMeta = styled.div`
  display: grid;
  gap: 2px;
`;

const SummaryLabel = styled.span`
  color: ${colors.grey.dark6};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
`;

const SummaryValue = styled.span`
  color: ${colors.black.light2};
  font-size: 13px;
  font-weight: 700;
`;

const SummaryDivider = styled.span`
  width: 1px;
  height: 26px;
  background: ${colors.grey.light12};
  justify-self: center;
`;

const SectionCard = styled.section`
  border: 1px solid ${colors.grey.light12};
  background: ${colors.white.default};
  padding: 11px;
  animation: ${slideIn} 220ms ease;
`;

const SectionHeader = styled.div`
  margin-bottom: 8px;
`;

const SectionSubTitle = styled.p`
  margin: 2px 0 0;
  color: ${colors.grey.dark6};
  font-size: 11px;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 12px;
  color: ${colors.black.dark1};
  letter-spacing: 0.08em;
  font-weight: 700;
  text-transform: uppercase;
`;

const DateRangeSelect = styled.select`
  width: 100%;
  border: 1px solid ${colors.grey.light12};
  background: ${colors.white.default};
  color: ${colors.black.light2};
  padding: 9px 10px;
  font-size: 13px;
  font-weight: 600;
  outline: none;

  &:focus {
    border-color: ${colors.black.light2};
  }
`;

const DateRangeRow = styled.div`
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const DateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.grey.dark6};
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid ${colors.grey.light12};
  border-radius: 0;
  outline: none;
  background: ${colors.white.default};

  &:focus {
    border-color: ${colors.black.light2};
  }
`;

const FieldsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const InlineButton = styled.button`
  border: 1px solid ${colors.grey.light12};
  background: ${colors.white.default};
  color: ${colors.black.light2};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 6px 9px;
  cursor: pointer;

  &:hover {
    background: ${colors.grey.light16};
  }
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 6px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FieldChip = styled.label<{ $isChecked: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 9px;
  border: 1px solid ${(props) => (props.$isChecked ? colors.black.light2 : colors.grey.light12)};
  background: ${(props) => (props.$isChecked ? colors.grey.light16 : colors.white.default)};
  transition:
    border-color 120ms ease,
    background-color 120ms ease;
  color: ${colors.black.light2};
  font-size: 12px;
  cursor: pointer;

  &:hover {
    border-color: ${colors.grey.dark4};
  }
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const FieldCheckMark = styled.span<{ $isChecked: boolean }>`
  width: 14px;
  height: 14px;
  border: 1px solid ${(props) => (props.$isChecked ? colors.black.light2 : colors.grey.dark4)};
  background: ${(props) => (props.$isChecked ? colors.black.light2 : colors.white.default)};
  color: ${colors.white.default};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
`;

const FieldText = styled.span`
  line-height: 1.25;
`;

const ValidationError = styled.div`
  margin-top: 6px;
  padding: 8px 10px;
  border: 1px solid #efb1aa;
  background: #fff6f4;
  color: #d32f2f;
  font-size: 12px;
  font-weight: 600;
`;

export default ExportWorkOrdersModal;
